// src/components/ReviewForm.jsx
import React, { useEffect, useMemo, useState } from "react";
import { createReviewPublic } from "../core/api/reviewsApi";
import { getProductsDropdown } from "../core/api/productsApi";

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

export default function ReviewForm({ onSuccess }) {
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    organization: "",
    address: "",
    product_slug: "",
    review: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        setLoadingProducts(true);
        const data = await getProductsDropdown();
        if (!mounted) return;
        setProducts(Array.isArray(data) ? data : []);
      } catch (e) {
        // silent but show dropdown empty
      } finally {
        if (mounted) setLoadingProducts(false);
      }
    })();

    return () => (mounted = false);
  }, []);

  const requiredErrors = useMemo(() => {
    const errors = {};
    if (!form.name.trim()) errors.name = "Name is required";
    if (!form.phone.trim()) errors.phone = "Phone is required";
    if (!form.email.trim()) errors.email = "Email is required";
    if (form.email.trim() && !isEmail(form.email)) errors.email = "Enter a valid email";
    if (!form.organization.trim()) errors.organization = "Organization is required";
    if (!form.product_slug.trim()) errors.product_slug = "Select a product";
    if (!form.review.trim()) errors.review = "Review is required";
    return errors;
  }, [form]);

  const hasErrors = Object.keys(requiredErrors).length > 0;

  const setField = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setMsg({ type: "", text: "" });
  };

  const reset = () => {
    setForm({
      name: "",
      phone: "",
      email: "",
      organization: "",
      address: "",
      product_slug: "",
      review: "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setMsg({ type: "", text: "" });

    if (hasErrors) {
      setMsg({ type: "error", text: "Please fill all required fields correctly." });
      return;
    }

    try {
      setSubmitting(true);
      await createReviewPublic(form);
      setMsg({ type: "success", text: "Thanks! Your review has been submitted." });
      reset();
      onSuccess?.();
    } catch (e2) {
      const apiMsg =
        e2?.response?.data?.detail ||
        (typeof e2?.response?.data === "string" ? e2.response.data : null) ||
        e2?.message ||
        "Failed to submit review";
      setMsg({ type: "error", text: apiMsg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
      <h3 className="text-lg font-semibold text-gray-900">Write a Review</h3>
      <p className="mt-1 text-sm text-gray-600">
        Your feedback helps us build better products.
      </p>

      {msg.text ? (
        <div
          className={[
            "mt-4 rounded-xl border p-3 text-sm",
            msg.type === "success"
              ? "border-green-200 bg-green-50 text-green-800"
              : "border-red-200 bg-red-50 text-red-800",
          ].join(" ")}
        >
          {msg.text}
        </div>
      ) : null}

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-semibold text-gray-700">Name *</label>
          <input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-900/10"
            placeholder="Your name"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-700">Phone *</label>
          <input
            value={form.phone}
            onChange={(e) => setField("phone", e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-900/10"
            placeholder="9876543210"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-700">Email *</label>
          <input
            value={form.email}
            onChange={(e) => setField("email", e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-900/10"
            placeholder="you@email.com"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-700">Organization *</label>
          <input
            value={form.organization}
            onChange={(e) => setField("organization", e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-900/10"
            placeholder="ABC Pvt Ltd"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-gray-700">Address</label>
          <input
            value={form.address}
            onChange={(e) => setField("address", e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-900/10"
            placeholder="City, State"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-gray-700">Product *</label>
          <select
            value={form.product_slug}
            onChange={(e) => setField("product_slug", e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-900/10 bg-white"
            disabled={loadingProducts}
          >
            <option value="">
              {loadingProducts ? "Loading products..." : "Select product"}
            </option>
            {products.map((p) => (
              <option key={p.slug} value={p.slug}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-gray-700">Your Review *</label>
          <textarea
            value={form.review}
            onChange={(e) => setField("review", e.target.value)}
            rows={4}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-gray-900/10"
            placeholder="Write your experience..."
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        className={[
          "mt-4 w-full rounded-xl px-4 py-2.5 text-sm font-semibold transition",
          submitting ? "bg-gray-200 text-gray-500 cursor-not-allowed" : "bg-gray-900 text-white hover:bg-gray-800",
        ].join(" ")}
      >
        {submitting ? "Submitting..." : "Submit Review"}
      </button>

      {/* Small helper */}
      <p className="mt-2 text-[12px] text-gray-500">
        * Required fields
      </p>
    </form>
  );
}
